import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import logoImg from "../PartyBoss-Logo5.svg";
import { SITE_WELCOME_FLASH } from "../api/constants";

/** Twemoji 1f64f (folded hands) — https://twemoji.twitter.com — CC-BY 4.0 */
const namasteHandsSrc = `${process.env.PUBLIC_URL || ""}/images/namaste-hands.svg`;
const DISPLAY_MS = 6000;
/** Custom event the {@link PromoPopup} listens to so it opens immediately after the welcome closes. */
export const WELCOME_CLOSED_EVENT = "partyboss:welcome-closed";

/** Fade duration when exiting (seconds for Framer). */
const EXIT_DURATION = 1.25;

/** After window `load`, wait two animation frames so the route paints behind the glass overlay. */
function subscribeDocumentReady(onReady) {
  if (typeof window === "undefined") return () => {};
  let finished = false;
  const invoke = () => {
    if (finished) return;
    finished = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(onReady);
    });
  };
  if (document.readyState === "complete") {
    invoke();
    return () => {};
  }
  window.addEventListener("load", invoke, { once: true });
  return () => window.removeEventListener("load", invoke);
}

/** Lotus-style blooms — petals open from centre. */
function CornerBloomFlower({ className, staggerDelay = 0, gradientId = "pf" }) {
  const petals = 8;
  const cx = 60;
  const cy = 60;
  const gradRef = `pb-welcome-petal-${gradientId}`;

  return (
    <svg viewBox="0 0 120 120" className={className} fill="currentColor" aria-hidden>
      <defs>
        <radialGradient id={gradRef} cx="50%" cy="30%" r="70%">
          <stop offset="0%" stopColor="rgb(252, 231, 243)" stopOpacity={1} />
          <stop offset="55%" stopColor="rgb(244, 114, 182)" stopOpacity={0.9} />
          <stop offset="100%" stopColor="rgb(192, 38, 211)" stopOpacity={0.45} />
        </radialGradient>
      </defs>
      <motion.g
        initial={{ scale: 0.15, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          delay: staggerDelay,
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      >
        {[...Array(petals)].map((_, i) => {
          const rotation = i * (360 / petals);
          return (
            <motion.ellipse
              key={rotation}
              cx={cx}
              cy={28}
              rx={15}
              ry={27}
              fill={`url(#${gradRef})`}
              stroke="rgba(255,255,255,0.5)"
              strokeWidth={1}
              style={{ transformOrigin: `${cx}px ${cy}px` }}
              transform={`rotate(${rotation} ${cx} ${cy})`}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: staggerDelay + 0.06 + i * 0.05,
                duration: 0.55,
                ease: [0.34, 1.56, 0.64, 1],
              }}
            />
          );
        })}
        <motion.circle
          cx={cx}
          cy={cy}
          r={14}
          fill="rgba(253,224,71,0.55)"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{
            delay: staggerDelay + 0.38,
            type: "spring",
            stiffness: 220,
            damping: 12,
          }}
        />
      </motion.g>
    </svg>
  );
}

function PartyBalloons({ className }) {
  return (
    <svg viewBox="0 0 100 140" className={className} aria-hidden fill="none">
      <motion.g
        initial={{ y: 18, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.15, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        <ellipse cx={32} cy={38} rx={22} ry={28} fill="#f472b6" stroke="#fff" strokeOpacity={0.5} strokeWidth={1} />
        <ellipse cx={66} cy={44} rx={20} ry={26} fill="#38bdf8" stroke="#fff" strokeOpacity={0.45} strokeWidth={1} />
        <ellipse cx={50} cy={68} rx={18} ry={23} fill="#fbbf24" stroke="#fff" strokeOpacity={0.45} strokeWidth={1} />
        <path
          d="M32 66 Q28 104 42 138 M66 70 Q74 112 62 138 M50 92 Q48 120 54 138"
          stroke="rgba(0,0,0,0.15)"
          strokeWidth={2}
          strokeLinecap="round"
        />
      </motion.g>
    </svg>
  );
}

/** Sparkly confetti specks behind the card. */
function ConfettiSpecks({ count = 40 }) {
  const colors = ["#fcd34d", "#f472b6", "#a78bfa", "#67e8f9", "#fbbf24"];
  return (
    <>
      {[...Array(count)].map((_, i) => {
        const delay = i * 0.038;
        const x = `${(Math.sin(i * 2.1) * 0.55 + 0.5) * 100}%`;
        const top = `${((i * 173) % 95) + 2}%`;
        const dur = 3.2 + (i % 6) * 0.38;
        return (
          <motion.span
            key={String(i)}
            className="pointer-events-none absolute h-2 w-2 rounded-full opacity-[0.95] shadow-sm"
            style={{
              left: x,
              top,
              backgroundColor: colors[i % colors.length],
            }}
            initial={{ opacity: 0.3, y: 0 }}
            animate={{
              opacity: [0.4, 1, 0.85],
              y: [-6, -28, -4],
              rotate: [0, 180],
            }}
            transition={{
              delay,
              duration: dur,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "easeInOut",
            }}
            aria-hidden
          />
        );
      })}
    </>
  );
}

/**
 * Birthday-party welcome splash (folded-hands emoji asset — no caption).
 */
export default function WelcomeOverlay() {
  const { pathname } = useLocation();
  const isAdmin = typeof pathname === "string" && pathname.startsWith("/admin");
  const [show, setShow] = useState(false);
  // null = not yet known (don't render or dispatch until we know); true/false = decided.
  const [enabled, setEnabled] = useState(null);

  // Fetch the SUPER_ADMIN-controlled toggle once on mount. Default to enabled if the API fails so
  // a transient backend hiccup doesn't kill the welcome experience.
  useEffect(() => {
    if (isAdmin) {
      setEnabled(false);
      return undefined;
    }
    let cancelled = false;
    axios
      .get(SITE_WELCOME_FLASH, { timeout: 4000 })
      .then((res) => {
        if (cancelled) return;
        setEnabled(res?.data?.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setEnabled(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin || enabled === null) {
      setShow(false);
      return undefined;
    }

    if (!enabled) {
      // Welcome flash turned off — dispatch the close event so PromoPopup opens immediately
      // instead of waiting for its 4s fallback timer.
      try {
        window.dispatchEvent(new CustomEvent(WELCOME_CLOSED_EVENT));
      } catch {
        /* ignore */
      }
      return undefined;
    }

    let dismissTimer;
    const unsubscribe = subscribeDocumentReady(() => {
      setShow(true);
      dismissTimer = window.setTimeout(() => {
        setShow(false);
        // Hand the baton to PromoPopup. Wait for the exit fade so it doesn't visually overlap.
        window.setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent(WELCOME_CLOSED_EVENT));
          } catch {
            /* IE legacy / SSR — ignore */
          }
        }, EXIT_DURATION * 1000);
      }, DISPLAY_MS);
    });

    return () => {
      unsubscribe();
      window.clearTimeout(dismissTimer);
      setShow(false);
    };
  }, [isAdmin, enabled]);

  useEffect(() => {
    if (show && !isAdmin) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
    return undefined;
  }, [show, isAdmin]);

  return (
    <AnimatePresence mode="wait">
      {show && !isAdmin ? (
        <motion.div
          key="party-boss-welcome-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pb-welcome-title"
          aria-describedby="pb-welcome-sub"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: EXIT_DURATION, ease: [0.4, 0, 0.2, 1] }}
          className="fixed inset-0 z-[10050] overflow-hidden"
        >
          {/* Site visible underneath — tinted glass */}
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-fuchsia-950/55 via-purple-950/40 to-pink-900/50 backdrop-blur-md"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_95%_75%_at_50%_15%,rgba(253,224,71,0.22),transparent_58%),radial-gradient(ellipse_85%_55%_at_85%_85%,rgba(244,114,182,0.22),transparent_52%),radial-gradient(ellipse_75%_45%_at_12%_88%,rgba(167,139,250,0.2),transparent_48%)]"
            aria-hidden
          />

          <ConfettiSpecks />

          {/* Large corner flowers */}
          <CornerBloomFlower
            className="pointer-events-none absolute left-2 top-2 h-[10.5rem] w-[10.5rem] text-pink-400 md:left-6 md:top-6 md:h-[13rem] md:w-[13rem]"
            staggerDelay={0.05}
            gradientId="tl"
          />
          <CornerBloomFlower
            className="pointer-events-none absolute right-2 top-2 h-[10.5rem] w-[10.5rem] scale-x-[-1] text-pink-400 md:right-6 md:top-6 md:h-[13rem] md:w-[13rem]"
            staggerDelay={0.12}
            gradientId="tr"
          />
          <CornerBloomFlower
            className="pointer-events-none absolute bottom-[5.5rem] left-2 h-[9rem] w-[9rem] text-fuchsia-400 md:bottom-[6.5rem] md:left-6 md:h-[11rem] md:w-[11rem]"
            staggerDelay={0.08}
            gradientId="bl"
          />
          <CornerBloomFlower
            className="pointer-events-none absolute bottom-[5.5rem] right-2 h-[9rem] w-[9rem] scale-x-[-1] text-fuchsia-400 md:bottom-[6.5rem] md:right-6 md:h-[11rem] md:w-[11rem]"
            staggerDelay={0.16}
            gradientId="br"
          />

          <PartyBalloons className="pointer-events-none absolute left-[6%] top-[38%] h-36 w-auto opacity-[0.95] md:left-[11%] md:top-[40%] md:h-44" />
          <PartyBalloons className="pointer-events-none absolute right-[4%] top-[40%] h-36 w-auto scale-x-[-1] opacity-[0.92] md:right-[9%] md:h-44" />

          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,transparent_50%,rgba(15,23,42,0.32)_82%,rgba(15,23,42,0.5)_100%)]"
            aria-hidden
          />

          <div className="relative z-[1] flex min-h-[100dvh] flex-col items-center justify-center px-4 pb-16 pt-[max(5rem,env(safe-area-inset-top))] sm:px-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.35 }}
              className="relative w-full max-w-xl"
            >
              <motion.div
                initial={{ scale: 0.9, y: 52, opacity: 0 }}
                animate={{ scale: 1, y: 0, opacity: 1 }}
                transition={{ delay: 0.05, duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
                className="relative mx-auto rounded-[2.25rem] border-[5px] border-pink-400 bg-gradient-to-br from-pink-50 via-amber-50 to-violet-100 px-8 py-10 shadow-[0_28px_85px_-6px_rgba(236,72,153,0.55),0_0_0_1px_rgba(255,255,255,0.9)_inset] sm:px-12 sm:py-12"
              >
                <div
                  className="pointer-events-none absolute -top-5 left-1/2 flex -translate-x-1/2 gap-4 text-2xl md:text-3xl"
                  aria-hidden
                >
                  <span role="presentation">✨</span>
                  <span role="presentation">🎉</span>
                  <span role="presentation">🎂</span>
                </div>

                <motion.div
                  initial={{ y: 64, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.14, duration: 0.78, ease: [0.22, 1, 0.36, 1] }}
                  className="mb-2 flex justify-center bg-transparent"
                >
                  <img
                    src={namasteHandsSrc}
                    alt=""
                    width={288}
                    height={288}
                    draggable={false}
                    className="h-[7rem] w-auto max-w-[min(100%,14rem)] bg-transparent object-contain sm:h-[8.25rem]"
                  />
                </motion.div>

                <div id="pb-welcome-title">
                  <span className="block px-1 text-center font-sans text-[1.7rem] font-black leading-tight tracking-tight text-slate-800 sm:text-[2rem] md:text-[2.2rem]">
                    <span className="bg-gradient-to-r from-pink-600 via-fuchsia-600 to-violet-700 bg-clip-text text-transparent">
                      Welcome to Party Boss
                    </span>
                  </span>
                  <p className="mx-auto mt-3 max-w-sm text-center text-sm font-semibold uppercase tracking-[0.2em] text-pink-600/95">
                    Where every celebration shines
                  </p>
                </div>

                <motion.div
                  id="pb-welcome-sub"
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.32, duration: 0.62, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-8 flex flex-col items-center gap-2"
                >
                  <img
                    src={logoImg}
                    alt="Party Boss logo"
                    className="h-[5rem] w-auto rounded-xl border-[5px] border-white bg-white px-5 py-2 shadow-xl sm:h-[5.75rem]"
                  />
                  <p className="text-center text-[0.68rem] font-bold uppercase tracking-[0.26em] text-fuchsia-600">
                    Private theatre birthdays &amp; celebrations
                  </p>
                </motion.div>
              </motion.div>

              <p className="mx-auto mt-8 max-w-sm text-center text-[0.6875rem] font-bold uppercase tracking-[0.26em] text-white drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]">
                Your celebration starts here — welcome!
              </p>
            </motion.div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
